import { useState, useEffect } from 'react';
import { Smartphone, CheckCircle2, Clock, XCircle, Loader2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import api, { studentApi } from '../lib/api';

const formatCurrency = (amount) => {
  return new Intl.NumberFormat('fr-SN', { style: 'currency', currency: 'XOF', maximumFractionDigits: 0 }).format(amount);
};

export default function MobilePaymentsPage() {
  const [payments, setPayments] = useState([]);
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [creating, setCreating] = useState(false);
  const [result, setResult] = useState(null);
  const [studentId, setStudentId] = useState('');
  const [amount, setAmount] = useState('');
  const [method, setMethod] = useState('wave');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [p, s] = await Promise.all([
        api.get('/mobile-payments'),
        studentApi.getAll({})
      ]);
      setPayments(p.data || []);
      setStudents(s.data || []);
    } catch (e) {
      console.error(e);
    }
    setLoading(false);
  };

  const handleCreate = async () => {
    if (!studentId || !amount) {
      toast.error('Remplissez tous les champs');
      return;
    }
    setCreating(true);
    try {
      const res = await api.post('/mobile-payments/create', {
        student_id: studentId,
        amount: parseFloat(amount),
        description: 'Frais de scolarité',
        payment_method: method
      });
      setResult(res.data);
      toast.success('Demande créée');
      fetchData();
    } catch (e) {
      toast.error('Erreur');
    }
    setCreating(false);
  };

  const handleConfirm = async (id) => {
    try {
      await api.put('/mobile-payments/' + id + '/confirm');
      toast.success('Confirmé');
      fetchData();
    } catch (e) {
      toast.error('Erreur');
    }
  };

  if (loading) {
    return <div className="flex justify-center p-8"><Loader2 className="w-8 h-8 animate-spin" /></div>;
  }

  const pending = payments.filter(p => p.status === 'pending').length;
  const completed = payments.filter(p => p.status === 'completed');
  const total = completed.reduce((s, p) => s + p.amount, 0);

  return (
    <div className="space-y-6" data-testid="mobile-payments-page">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold">Paiements Mobiles</h1>
          <p className="text-muted-foreground">Wave & Orange Money</p>
        </div>
        <Dialog open={showDialog} onOpenChange={setShowDialog}>
          <DialogTrigger asChild>
            <Button><Smartphone className="w-4 h-4 mr-2" />Nouveau</Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{result ? 'Demande créée' : 'Nouveau paiement'}</DialogTitle>
            </DialogHeader>
            {result ? (
              <div className="space-y-4">
                <div className="p-4 bg-green-50 rounded-lg text-center">
                  <CheckCircle2 className="w-10 h-10 text-green-600 mx-auto" />
                  <p className="text-xl font-bold mt-2">{formatCurrency(result.amount)}</p>
                  <p className="text-sm">Réf: {result.payment_id.slice(0,8).toUpperCase()}</p>
                </div>
                <div className="text-sm">
                  <p className="font-medium">{result.instructions?.title}</p>
                  <ol className="list-decimal ml-4 mt-2 space-y-1">
                    {result.instructions?.steps?.map((s, i) => <li key={i}>{s}</li>)}
                  </ol>
                </div>
                <Button onClick={() => { setResult(null); setShowDialog(false); }} className="w-full">Fermer</Button>
              </div>
            ) : (
              <div className="space-y-4">
                <div>
                  <Label>Élève</Label>
                  <Select value={studentId} onValueChange={setStudentId}>
                    <SelectTrigger><SelectValue placeholder="Sélectionner" /></SelectTrigger>
                    <SelectContent>
                      {students.map(s => <SelectItem key={s.id} value={s.id}>{s.first_name} {s.last_name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Montant (FCFA)</Label>
                  <Input type="number" value={amount} onChange={e => setAmount(e.target.value)} placeholder="50000" />
                </div>
                <div>
                  <Label>Méthode</Label>
                  <div className="grid grid-cols-2 gap-2 mt-1">
                    <Button type="button" variant={method === 'wave' ? 'default' : 'outline'} onClick={() => setMethod('wave')}>🌊 Wave</Button>
                    <Button type="button" variant={method === 'orange_money' ? 'default' : 'outline'} onClick={() => setMethod('orange_money')}>🟠 Orange</Button>
                  </div>
                </div>
                <Button onClick={handleCreate} disabled={creating} className="w-full">
                  {creating && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}Créer
                </Button>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid grid-cols-3 gap-4">
        <Card><CardContent className="p-4 flex items-center gap-3">
          <Clock className="w-8 h-8 text-amber-500" />
          <div><p className="text-sm text-muted-foreground">En attente</p><p className="text-xl font-bold">{pending}</p></div>
        </CardContent></Card>
        <Card><CardContent className="p-4 flex items-center gap-3">
          <CheckCircle2 className="w-8 h-8 text-green-500" />
          <div><p className="text-sm text-muted-foreground">Confirmés</p><p className="text-xl font-bold">{completed.length}</p></div>
        </CardContent></Card>
        <Card><CardContent className="p-4 flex items-center gap-3">
          <Smartphone className="w-8 h-8 text-primary" />
          <div><p className="text-sm text-muted-foreground">Total</p><p className="text-xl font-bold">{formatCurrency(total)}</p></div>
        </CardContent></Card>
      </div>

      <Card>
        <CardHeader><CardTitle>Historique</CardTitle></CardHeader>
        <CardContent>
          {payments.length === 0 ? (
            <p className="text-center text-muted-foreground py-8">Aucun paiement</p>
          ) : (
            <div className="space-y-2">
              {payments.map(p => (
                <div key={p.id} className="flex items-center justify-between p-3 border rounded-lg">
                  <div>
                    <p className="font-medium">{p.student_name}</p>
                    <p className="text-xs text-muted-foreground">Réf: {p.id.slice(0,8).toUpperCase()}</p>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="text-right">
                      <p className="font-bold">{formatCurrency(p.amount)}</p>
                      <Badge variant={p.status === 'completed' ? 'default' : 'secondary'}>
                        {p.status === 'completed' ? 'Confirmé' : 'En attente'}
                      </Badge>
                    </div>
                    {p.status === 'pending' && <Button size="sm" onClick={() => handleConfirm(p.id)}>Confirmer</Button>}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
